import { useEffect, useRef, useState } from "react";
import type { KnowledgeNodeDetail } from "@/api/memory";
import { updateNode } from "@/api/memory";
import {
  createAdaptedWorkSkill,
  type StepReplacement,
  type WorkSkillStep,
} from "@/api/work-skills";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/text-input";
import { translate, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsSkillsMessages } from "@/i18n/messages/settings-skills";
import {
  useSettingsDraftState,
  useResetSettingsDraft,
} from "./settings-draft-state";
export function WorkSkillEditor({
  detail,
  steps,
  onCancel,
  onSaved,
}: {
  detail: KnowledgeNodeDetail;
  steps: WorkSkillStep[];
  onCancel: () => void;
  onSaved: (saved: KnowledgeNodeDetail) => void;
}) {
  const t = useT(settingsSkillsMessages);
  const tCommon = useT(commonMessages);
  const originalTitle = String(detail.metadata.title ?? detail.name);
  const prefix = `skill:${detail.id}:`;
  const reset = useResetSettingsDraft();
  const [title, setTitle, initializeTitle] = useSettingsDraftState(
    `${prefix}title`,
    originalTitle,
  );
  const [drafts, setDrafts, initializeDrafts] = useSettingsDraftState<
    Record<string, string>
  >(`${prefix}steps`, () =>
    Object.fromEntries(steps.map((step) => [step.step_id, step.text])),
  );
  const writing = useRef(false);
  const queued = useRef(false);
  const lastSaved = useRef<string | null>(null);
  const latestDetail = useRef(detail);
  latestDetail.current = detail;
  const latestForm = useRef({ title, drafts });
  latestForm.current = { title, drafts };
  const cancelled = useRef(false);
  const latestSave = useRef<() => Promise<void>>(async () => {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const changes: StepReplacement[] = steps.flatMap((step) => {
    const text = drafts[step.step_id]?.trim() ?? "";
    return text && text !== step.text
      ? [{ op: "replace", step_id: step.step_id, text }]
      : [];
  });
  const canSave =
    title.trim().length > 0 &&
    steps.every((step) => drafts[step.step_id]?.trim()) &&
    (detail.shared
      ? changes.length > 0
      : changes.length > 0 || title.trim() !== originalTitle);

  async function save() {
    if (writing.current) {
      queued.current = true;
      return;
    }
    if (cancelled.current) return;
    const snapshot = JSON.stringify({ title, drafts });
    if (!title.trim() || steps.some((step) => !drafts[step.step_id]?.trim())) {
      setError(translate(settingsSkillsMessages, "enterNameAndSteps"));
      return;
    }
    if (!canSave || lastSaved.current === snapshot) return;
    writing.current = true;
    setSaving(true);
    setError("");
    try {
      let saved: KnowledgeNodeDetail;
      if (detail.shared) {
        const activityId = detail.edges.find(
          (edge) =>
            edge.relationship_type === "for_activity" &&
            edge.source === detail.id,
        )?.target;
        const externalId = detail.metadata.external_id;
        const version = detail.metadata.version;
        if (
          !activityId ||
          typeof externalId !== "string" ||
          typeof version !== "number"
        ) {
          throw new Error(translate(settingsSkillsMessages, "cannotCustomize"));
        }
        saved = await createAdaptedWorkSkill({
          title: title.trim(),
          libraryRef: `${externalId}@${version}`,
          activityId,
          overlay: changes,
        });
      } else {
        const existing = Array.isArray(latestDetail.current.metadata.overlay)
          ? (latestDetail.current.metadata.overlay as Array<
              Record<string, unknown>
            >)
          : [];
        const changedIds = new Set(changes.map((item) => item.step_id));
        const overlay = [
          ...existing.filter(
            (item) =>
              item.op !== "replace" || !changedIds.has(String(item.step_id)),
          ),
          ...changes,
        ];
        saved = await updateNode(detail.id, {
          expected_version: latestDetail.current.version,
          metadata: { title: title.trim(), overlay },
        });
      }
      lastSaved.current = snapshot;
      latestDetail.current = saved;
      if (JSON.stringify(latestForm.current) === snapshot) {
        initializeTitle(String(saved.metadata.title ?? saved.name));
        initializeDrafts(drafts);
        reset(prefix);
      }
      onSaved(saved);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : translate(settingsSkillsMessages, "saveFailed"),
      );
    } finally {
      writing.current = false;
      setSaving(false);
      if (queued.current) {
        queued.current = false;
        void latestSave.current();
      }
    }
  }
  latestSave.current = save;
  useEffect(
    () => () => {
      if (!detail.shared) void latestSave.current();
    },
    [detail.shared],
  );
  function autoSave() {
    if (!detail.shared) void save();
  }

  return (
    <div className="space-y-4" data-testid="work-skill-editor">
      <p className="text-appkit-mini text-secondary">
        {detail.shared ? t("sharedIntro") : t("personalIntro")}
      </p>
      <label className="block space-y-1 text-appkit-mini text-secondary">
        <span>{t("name")}</span>
        <TextInput
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={autoSave}
          onKeyDown={(event) => {
            if (event.key === "Escape" && title !== originalTitle) {
              event.preventDefault();
              event.stopPropagation();
              setTitle(originalTitle);
              setError("");
            }
            if (
              event.key === "Enter" &&
              !event.nativeEvent.isComposing &&
              !detail.shared
            ) {
              event.preventDefault();
              autoSave();
            }
          }}
          data-testid="work-skill-title-input"
        />
      </label>
      {steps.map((step, index) => (
        <label
          key={step.step_id}
          className="block space-y-1 text-appkit-mini text-secondary"
        >
          <span>{t("step", { number: index + 1 })}</span>
          <textarea
            value={drafts[step.step_id] ?? ""}
            onChange={(event) =>
              setDrafts((current) => ({
                ...current,
                [step.step_id]: event.target.value,
              }))
            }
            onBlur={autoSave}
            onKeyDown={(event) => {
              if (
                event.key === "Escape" &&
                drafts[step.step_id] !== step.text
              ) {
                event.preventDefault();
                event.stopPropagation();
                setDrafts((current) => ({
                  ...current,
                  [step.step_id]: step.text,
                }));
              }
              if (
                event.key === "Enter" &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing &&
                !detail.shared
              ) {
                event.preventDefault();
                autoSave();
              }
            }}
            rows={2}
            className="w-full rounded-control border border-glass-stroke bg-glass-fill px-3 py-2 text-appkit-caption text-primary"
            data-testid={`work-skill-step-${step.step_id}`}
          />
        </label>
      ))}
      {error && (
        <p role="alert" className="text-appkit-mini text-danger">
          {error}
        </p>
      )}
      {!detail.shared && saving && (
        <p role="status" className="text-appkit-small text-muted">
          {t("saving")}
        </p>
      )}
      <div className="flex gap-2">
        {detail.shared && (
          <Button
            size="sm"
            onClick={() => void save()}
            disabled={!canSave || saving}
            data-testid="work-skill-save"
          >
            {saving ? t("saving") : t("saveMyVersion")}
          </Button>
        )}
        {detail.shared && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              cancelled.current = true;
              reset(prefix);
              onCancel();
            }}
            disabled={saving}
            data-testid="work-skill-cancel"
          >
            {tCommon("cancel")}
          </Button>
        )}
      </div>
    </div>
  );
}
